import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import {
  Settings, Clock, Users, Shield, Monitor, Save, RefreshCw, Plus, Trash2,
  Edit2, ChevronDown, ChevronRight, ToggleLeft, ToggleRight, Loader2, Search,
  BarChart3, DollarSign, LayoutGrid, Table as TableIcon, Box, Film,
  Palette, Sparkles, Megaphone, Layers, Package, Eye, Rocket, CheckCircle2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";

const TABS = [
  { key: "rules", label: "Deliverable Rules", icon: Clock },
  { key: "kickoff", label: "Kickoff Phase", icon: Shield },
  { key: "capacity", label: "Studio Capacity", icon: Users },
  { key: "staffing", label: "Staffing Allocation", icon: BarChart3 },
  { key: "buffers", label: "Buffers / Review", icon: Shield },
  { key: "display", label: "Display / Proposal", icon: Monitor },
];

const CATEGORIES = [
  "3D Modeling", "Exterior Renders", "Interior Renders", "Exterior 360s",
  "Interior 360s", "Animation", "VEXOS", "Marketing"
];

const PHASES = [
  "Phase 0 — Ramp-Up / Queue", "Phase 1 — Kickoff",
  "Phase 2 — Ramp-Up & Project Setup", "Phase 3 — Modeling / Scene Dev",
  "Phase 4 — Initial Production", "Phase 5 — Review + Revisions",
  "Phase 6 — Dependent Deliverables", "Phase 7 — Finalization / Delivery"
];

const DEP_TYPES = [
  "none", "requires_model_ready", "requires_scene_ready",
  "requires_stills_approved", "requires_design_approved",
  "requires_floorplate_ready", "requires_exterior_context_ready",
  "requires_previous_phase_complete"
];

// Color tokens per category — all Tailwind classes used here are static so JIT picks them up.
const CATEGORY_STYLE = {
  "3D Modeling":         { bar: "bg-blue-500",   ring: "ring-blue-500/30",   bg: "bg-blue-500/[0.04]",   text: "text-blue-600 dark:text-blue-400",     chip: "bg-blue-500/10 text-blue-600 border-blue-500/20",     icon: Box },
  "Exterior Renders":    { bar: "bg-purple-500", ring: "ring-purple-500/30", bg: "bg-purple-500/[0.04]", text: "text-purple-600 dark:text-purple-400", chip: "bg-purple-500/10 text-purple-600 border-purple-500/20", icon: Palette },
  "Interior Renders":    { bar: "bg-indigo-500", ring: "ring-indigo-500/30", bg: "bg-indigo-500/[0.04]", text: "text-indigo-600 dark:text-indigo-400", chip: "bg-indigo-500/10 text-indigo-600 border-indigo-500/20", icon: Palette },
  "Exterior 360s":       { bar: "bg-emerald-500",ring: "ring-emerald-500/30",bg: "bg-emerald-500/[0.04]",text: "text-emerald-600 dark:text-emerald-400",chip: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",icon: Eye },
  "Interior 360s":       { bar: "bg-emerald-500",ring: "ring-emerald-500/30",bg: "bg-emerald-500/[0.04]",text: "text-emerald-600 dark:text-emerald-400",chip: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",icon: Eye },
  "Animation":           { bar: "bg-orange-500", ring: "ring-orange-500/30", bg: "bg-orange-500/[0.04]", text: "text-orange-600 dark:text-orange-400", chip: "bg-orange-500/10 text-orange-600 border-orange-500/20", icon: Film },
  "VEXOS":               { bar: "bg-cyan-500",   ring: "ring-cyan-500/30",   bg: "bg-cyan-500/[0.04]",   text: "text-cyan-600 dark:text-cyan-400",     chip: "bg-cyan-500/10 text-cyan-600 border-cyan-500/20",     icon: Layers },
  "Marketing":           { bar: "bg-amber-500",  ring: "ring-amber-500/30",  bg: "bg-amber-500/[0.04]",  text: "text-amber-600 dark:text-amber-400",   chip: "bg-amber-500/10 text-amber-600 border-amber-500/20",   icon: Megaphone },
};
const DEFAULT_CATEGORY_STYLE = { bar: "bg-zinc-400", ring: "ring-zinc-400/30", bg: "bg-zinc-400/[0.04]", text: "text-zinc-600", chip: "bg-zinc-200 text-zinc-700 border-zinc-300", icon: Package };

// 8 phase groups. The resolver `findPhaseGroupForRule(rule)` uses category +
// deliverable name keywords (per spec) so VEXOS items don't bleed into Visualization,
// 360 tours don't sit under Animation, and marketing collateral has its own bucket.
// `phase_default` (0–7) is still the underlying scheduling order — it's used as a
// fallback when category/name don't disambiguate.
const PHASE_GROUPS = [
  { key: "kickoff",       label: "Kickoff",             subtitle: "Project setup, ramp-up, alignment",      phases: [0, 1, 2], icon: Rocket,        accent: "text-slate-600" },
  { key: "modeling",      label: "Modeling",            subtitle: "3D modeling & scene development",        phases: [3],       icon: Box,           accent: "text-blue-600" },
  { key: "visualization", label: "Visualization",       subtitle: "Still renders only",                     phases: [4, 5],    icon: Palette,       accent: "text-purple-600" },
  { key: "tours_360",     label: "360 Tours",           subtitle: "360 spatial tours & virtual experiences",phases: [6],       icon: Eye,           accent: "text-emerald-600" },
  { key: "animation",     label: "Animation",           subtitle: "Animations, cinematic films, video",     phases: [6],       icon: Film,          accent: "text-orange-600" },
  { key: "vexos",         label: "VEXOS",               subtitle: "Interactive sales platform deliverables",phases: [4, 5, 6], icon: Layers,        accent: "text-cyan-600" },
  { key: "marketing",     label: "Marketing Collateral",subtitle: "Web, brochures, paid media, social",     phases: [4, 5, 6], icon: Megaphone,     accent: "text-amber-600" },
  { key: "delivery",      label: "Delivery",            subtitle: "Finalization, exports, handoff",         phases: [7],       icon: CheckCircle2,  accent: "text-cyan-600" },
];

// Helper: does a label string contain any of the supplied keywords (case-insensitive)?
const _hasAny = (s, keywords) => {
  if (!s) return false;
  const lower = s.toLowerCase();
  return keywords.some(k => lower.includes(k));
};

/**
 * Surgical phase-group resolver — the single source of truth for assigning a rule
 * to one of the 8 phase columns. Order matters: most-specific overrides win first.
 * Accepts either a full rule object OR a numeric phase_default (legacy callers).
 */
function findPhaseGroupForRule(ruleOrPhase) {
  // Legacy callers may pass a number — wrap it.
  const rule = typeof ruleOrPhase === "number" ? { phase_default: ruleOrPhase } : (ruleOrPhase || {});
  const name = (rule.deliverable_name || "").trim();
  const category = (rule.category || "").trim();
  const phase = rule.phase_default;
  const lname = name.toLowerCase();
  const lcat = category.toLowerCase();

  // 1. VEXOS — highest priority. Either category=VEXOS OR name signals platform/interactive deliverable.
  const vexosNameSignals = [
    "vexos", "interactive site plan", "interactive lot", "interactive experience",
    "interactive virtual sales", "virtual sales gallery", "sales gallery",
    "platform setup", "platform deployment", "inventory programming",
    "floorplate overlay", "availability filter", "pricing filter",
    "neighborhood explorer", "map widget", "location widget"
  ];
  if (lcat === "vexos" || _hasAny(lname, vexosNameSignals)) {
    return PHASE_GROUPS.find(g => g.key === "vexos");
  }
  // "Immersive Virtual Tour" → VEXOS only when explicitly labeled immersive/platform.
  if (_hasAny(lname, ["immersive virtual tour", "immersive platform", "platform experience"])) {
    return PHASE_GROUPS.find(g => g.key === "vexos");
  }

  // 2. 360 Tours — categories Interior 360s / Exterior 360s OR name contains "360" / virtual tour
  //    (but NOT VEXOS-flavored — already handled above).
  const tour360NameSignals = ["360 tour", "interior 360", "exterior 360", "amenity 360", "residential 360", " 360 ", "360s"];
  if (lcat === "interior 360s" || lcat === "exterior 360s" || _hasAny(lname, tour360NameSignals)) {
    return PHASE_GROUPS.find(g => g.key === "tours_360");
  }
  // Generic "Virtual Tour" → 360 Tours unless flagged VEXOS/sales gallery (caught above).
  if (_hasAny(lname, ["virtual tour"])) {
    return PHASE_GROUPS.find(g => g.key === "tours_360");
  }

  // 3. Animation — categories Animation OR motion/video/film keywords.
  const animationNameSignals = [
    "animation", "cinematic film", "video animation", "video production",
    "30sec", "60sec", "90sec", "120sec"
  ];
  if (lcat === "animation" || _hasAny(lname, animationNameSignals)) {
    // Social Media Cutdowns are AMBIGUOUS — treat as Animation if explicitly tied to motion category,
    // otherwise Marketing (handled below).
    return PHASE_GROUPS.find(g => g.key === "animation");
  }

  // 4. Marketing Collateral — category Marketing OR collateral/web/brochure/social keywords.
  const marketingNameSignals = [
    "website", "landing page", "brochure", "collateral", "gallery", "floor plan",
    "site plan", "paid media", "digital marketing", "media management",
    "social media content", "social media cutdowns", "pitch deck", "presentation",
    "one-pager", "graphic"
  ];
  if (lcat === "marketing" || _hasAny(lname, marketingNameSignals)) {
    return PHASE_GROUPS.find(g => g.key === "marketing");
  }

  // 5. Modeling — category 3D Modeling OR modeling keywords.
  const modelingNameSignals = [
    "3d modeling", "interior modeling", "exterior modeling", "site modeling",
    "landscape", "furnishing", "house model", "high rise model", "masterplan model",
    "mid level model", "interior unit"
  ];
  if (lcat === "3d modeling" || _hasAny(lname, modelingNameSignals)) {
    return PHASE_GROUPS.find(g => g.key === "modeling");
  }

  // 6. Visualization (still renders only) — category Exterior/Interior Renders OR render keywords.
  const renderNameSignals = [
    "render", "exterior render", "interior render", "masterplan render",
    "bathroom", "bedroom", "living space", "amenities", "amenity"
  ];
  if (lcat === "exterior renders" || lcat === "interior renders" || _hasAny(lname, renderNameSignals)) {
    return PHASE_GROUPS.find(g => g.key === "visualization");
  }

  // 7. Fall back to numeric phase_default — Kickoff (0-2), Modeling (3),
  //    Visualization (4-5), tours/animation (6), Delivery (7).
  if (phase === 7) return PHASE_GROUPS.find(g => g.key === "delivery");
  if (phase === 6) return PHASE_GROUPS.find(g => g.key === "tours_360");
  if (phase === 4 || phase === 5) return PHASE_GROUPS.find(g => g.key === "visualization");
  if (phase === 3) return PHASE_GROUPS.find(g => g.key === "modeling");
  return PHASE_GROUPS.find(g => g.key === "kickoff");
}

function formatDepLabel(dep) {
  if (!dep || dep === "none") return null;
  return dep.replace("requires_", "").replaceAll("_", " ");
}

export default function TimelineSettingsPage() {
  const [tab, setTab] = useState("rules");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rules, setRules] = useState([]);
  const [roleBuckets, setRoleBuckets] = useState([]);
  const [studioSettings, setStudioSettings] = useState({});
  const [bufferSettings, setBufferSettings] = useState({});
  const [displaySettings, setDisplaySettings] = useState({});
  const [staffingSettings, setStaffingSettings] = useState({ enabled: true, brackets: [], fallback_max_artists: 2, notes: "" });
  const [kickoffSettings, setKickoffSettings] = useState({
    phase_name: "kickoff", display_name: "Kickoff", order_index: 1,
    active: true, default_min_business_days: 3, default_max_business_days: 5,
    editable: true, proposal_visible: true, project_visible: true,
    include_in_total_timeline: true, internal_notes: "",
    complexity_scaling: { simple: 1.0, standard: 1.0, luxury: 1.2, ultra_luxury: 1.5 },
    rush_reduction_percent: 30, stakeholder_scaling_enabled: false,
    stakeholder_base_count: 3, stakeholder_extra_days_per: 0.5,
  });
  const [search, setSearch] = useState("");
  const [editingRule, setEditingRule] = useState(null);
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  // Rules tab view mode: "timeline" (default visual map), "table" (advanced)
  const [rulesView, setRulesView] = useState("timeline");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, bucketsRes, studioRes, bufferRes, displayRes, staffingRes, kickoffRes] = await Promise.all([
        apiClient.get("/timeline-engine/deliverable-rules"),
        apiClient.get("/timeline-engine/role-buckets"),
        apiClient.get("/timeline-engine/settings/studio"),
        apiClient.get("/timeline-engine/settings/buffer"),
        apiClient.get("/timeline-engine/settings/display"),
        apiClient.get("/timeline-engine/settings/staffing"),
        apiClient.get("/timeline-engine/settings/kickoff"),
      ]);
      setRules(rulesRes.data);
      setRoleBuckets(bucketsRes.data);
      setStudioSettings(studioRes.data);
      setBufferSettings(bufferRes.data);
      setDisplaySettings(displayRes.data);
      setStaffingSettings(staffingRes.data);
      setKickoffSettings(kickoffRes.data);
    } catch {
      toast.error("Failed to load timeline settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveStudioSettings = async () => {
    setSaving(true);
    try {
      await apiClient.put("/timeline-engine/settings/studio", studioSettings);
      toast.success("Studio settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const saveBufferSettings = async () => {
    setSaving(true);
    try {
      await apiClient.put("/timeline-engine/settings/buffer", bufferSettings);
      toast.success("Buffer settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const saveDisplaySettings = async () => {
    setSaving(true);
    try {
      await apiClient.put("/timeline-engine/settings/display", displaySettings);
      toast.success("Display settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const saveStaffingSettings = async () => {
    setSaving(true);
    try {
      await apiClient.put("/timeline-engine/settings/staffing", staffingSettings);
      toast.success("Staffing settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const saveKickoffSettings = async () => {
    setSaving(true);
    try {
      await apiClient.put("/timeline-engine/settings/kickoff", kickoffSettings);
      toast.success("Kickoff phase settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const saveRule = async (ruleData) => {
    setSaving(true);
    try {
      if (ruleData.id) {
        await apiClient.patch(`/timeline-engine/deliverable-rules/${ruleData.id}`, ruleData);
        toast.success("Rule updated");
      } else {
        await apiClient.post("/timeline-engine/deliverable-rules", ruleData);
        toast.success("Rule created");
      }
      setShowRuleDialog(false);
      setEditingRule(null);
      fetchAll();
    } catch { toast.error("Failed to save rule"); }
    finally { setSaving(false); }
  };

  const deleteRule = async (ruleId, ruleName) => {
    const label = ruleName ? `"${ruleName}"` : "this rule";
    // Two-step confirmation so reps can't single-click a deliverable out
    // of the library by accident. Existing project assignments are NOT
    // touched server-side — only the library row is removed.
    if (!window.confirm(`Permanently delete ${label}?\n\nThis removes the rule from the Timeline Engine library so it can no longer be added to new projects. Existing project assignments stay intact.`)) {
      return;
    }
    try {
      await apiClient.delete(`/timeline-engine/deliverable-rules/${ruleId}`);
      toast.success("Rule deleted");
      fetchAll();
    } catch { toast.error("Failed to delete"); }
  };

  const toggleRule = async (rule) => {
    try {
      await apiClient.patch(`/timeline-engine/deliverable-rules/${rule.id}`, { active: !rule.active });
      fetchAll();
    } catch { toast.error("Failed to toggle"); }
  };

  const filteredRules = rules.filter(r =>
    !search || r.deliverable_name.toLowerCase().includes(search.toLowerCase()) ||
    r.category.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6" data-testid="timeline-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light tracking-tight">Timeline Engine Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">Configure deliverable rules, studio capacity, and estimation logic</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 border-b border-border pb-0">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`tab-${t.key}`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* TAB: Deliverable Rules */}
      {tab === "rules" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="relative flex-1 max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search rules..."
                className="pl-9"
                data-testid="rules-search"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* View Mode Segmented Control */}
              <div className="flex items-center bg-muted/40 border border-border rounded-md p-0.5" data-testid="rules-view-toggle">
                <button
                  onClick={() => setRulesView("timeline")}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ${rulesView === "timeline" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid="view-timeline-btn"
                >
                  <LayoutGrid className="w-3.5 h-3.5" /> Timeline View
                </button>
                <button
                  onClick={() => setRulesView("table")}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ${rulesView === "table" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid="view-table-btn"
                >
                  <TableIcon className="w-3.5 h-3.5" /> Advanced Table
                </button>
              </div>
              <Badge variant="outline">{filteredRules.length} rules</Badge>
              <Button size="sm" onClick={() => { setEditingRule(null); setShowRuleDialog(true); }} data-testid="add-rule-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Rule
              </Button>
            </div>
          </div>

          {/* TIMELINE VIEW — phase-grouped cards */}
          {rulesView === "timeline" && (
            <TimelineView
              rules={filteredRules}
              onCardClick={(rule) => { setEditingRule(rule); setShowRuleDialog(true); }}
              onToggle={toggleRule}
              onDelete={deleteRule}
            />
          )}

          {/* ADVANCED TABLE VIEW — original dense table for power users */}
          {rulesView === "table" && (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/40">
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground">Deliverable</th>
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground">Category</th>
                  <th className="text-center p-3 text-xs font-medium text-muted-foreground">Phase</th>
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground">Role Bucket</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground">Days/Unit</th>
                  <th className="text-center p-3 text-xs font-medium text-muted-foreground">Max Artists</th>
                  <th className="text-center p-3 text-xs font-medium text-muted-foreground">Dependency</th>
                  <th className="text-center p-3 text-xs font-medium text-muted-foreground">Active</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground w-20"></th>
                </tr>
              </thead>
              <tbody>
                {filteredRules.map(rule => (
                  <tr key={rule.id} className={`border-t border-border hover:bg-muted/20 ${!rule.active ? "opacity-50" : ""}`}>
                    <td className="p-3 text-sm font-medium">{rule.deliverable_name}</td>
                    <td className="p-3 text-sm text-muted-foreground">{rule.category}</td>
                    <td className="p-3 text-center">
                      <Badge variant="outline" className="text-xs">{rule.phase_default}</Badge>
                    </td>
                    <td className="p-3 text-sm text-muted-foreground">{rule.role_bucket_required}</td>
                    <td className="p-3 text-right text-sm font-mono">{rule.duration_per_unit}d</td>
                    <td className="p-3 text-center text-sm font-mono">
                      {rule.max_parallel_artists != null ? rule.max_parallel_artists : "—"}
                    </td>
                    <td className="p-3 text-center">
                      <Badge variant="secondary" className="text-xs">
                        {rule.dependency_type === "none" ? "—" : rule.dependency_type.replace("requires_", "").replaceAll("_", " ")}
                      </Badge>
                    </td>
                    <td className="p-3 text-center">
                      <button onClick={() => toggleRule(rule)} className="text-muted-foreground hover:text-foreground">
                        {rule.active ? <ToggleRight className="w-5 h-5 text-green-600" /> : <ToggleLeft className="w-5 h-5" />}
                      </button>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingRule(rule); setShowRuleDialog(true); }}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deleteRule(rule.id, rule.deliverable_name)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      {/* TAB: Studio Capacity */}
      {tab === "capacity" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Working Hours</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <SettingField label="Working Days/Week" value={studioSettings.working_days_per_week}
                onChange={v => setStudioSettings({ ...studioSettings, working_days_per_week: parseInt(v) || 5 })} type="number" />
              <SettingField label="Hours/Day" value={studioSettings.hours_per_day}
                onChange={v => setStudioSettings({ ...studioSettings, hours_per_day: parseInt(v) || 8 })} type="number" />
              <SettingToggle label="Weekends Included" value={studioSettings.weekends_included}
                onChange={v => setStudioSettings({ ...studioSettings, weekends_included: v })} />
              <SettingToggle label="Overtime Allowed" value={studioSettings.overtime_allowed}
                onChange={v => setStudioSettings({ ...studioSettings, overtime_allowed: v })} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Utilization Thresholds</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <SettingField label="Standard Utilization (%)" value={studioSettings.standard_utilization_threshold}
                onChange={v => setStudioSettings({ ...studioSettings, standard_utilization_threshold: parseInt(v) || 75 })} type="number" />
              <SettingField label="Max Safe Utilization (%)" value={studioSettings.max_safe_utilization_threshold}
                onChange={v => setStudioSettings({ ...studioSettings, max_safe_utilization_threshold: parseInt(v) || 90 })} type="number" />
              <SettingField label="Rush Threshold (%)" value={studioSettings.rush_threshold}
                onChange={v => setStudioSettings({ ...studioSettings, rush_threshold: parseInt(v) || 95 })} type="number" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Scheduling</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <SettingToggle label="Queue/Ramp-Up Logic Enabled" value={studioSettings.queue_ramp_logic_enabled}
                onChange={v => setStudioSettings({ ...studioSettings, queue_ramp_logic_enabled: v })} />
              <SettingField label="Internal QA Default Days" value={studioSettings.internal_qa_default_days}
                onChange={v => setStudioSettings({ ...studioSettings, internal_qa_default_days: parseInt(v) || 2 })} type="number" />
              <SettingField label="Min Scheduling Block (days)" value={studioSettings.minimum_scheduling_block_days}
                onChange={v => setStudioSettings({ ...studioSettings, minimum_scheduling_block_days: parseFloat(v) || 0.5 })} type="number" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Role Buckets</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {roleBuckets.map(b => (
                <div key={b.id} className="flex items-center justify-between p-2 rounded bg-muted/30">
                  <div>
                    <span className="text-sm font-medium">{b.name}</span>
                    <p className="text-xs text-muted-foreground">{b.description}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">{b.default_daily_capacity_units}h/day</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
          <div className="col-span-full flex justify-end">
            <Button onClick={saveStudioSettings} disabled={saving} data-testid="save-studio-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Studio Settings
            </Button>
          </div>
        </div>
      )}

      {/* TAB: Staffing Allocation */}
      {tab === "staffing" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Staffing Allocation Brackets</CardTitle>
                <SettingToggle label="Enabled" value={staffingSettings.enabled}
                  onChange={v => setStaffingSettings({ ...staffingSettings, enabled: v })} />
              </div>
              <p className="text-sm text-muted-foreground">
                Maps proposal total value to max concurrent artists for timeline estimation.
                This is a staffing allowance / concurrency cap — not a guaranteed assignment.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/40">
                      <th className="text-left p-3 text-xs font-medium text-muted-foreground">Min Value ($)</th>
                      <th className="text-left p-3 text-xs font-medium text-muted-foreground">Max Value ($)</th>
                      <th className="text-center p-3 text-xs font-medium text-muted-foreground">Max Artists</th>
                      <th className="text-left p-3 text-xs font-medium text-muted-foreground">Label</th>
                      <th className="text-right p-3 text-xs font-medium text-muted-foreground w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(staffingSettings.brackets || []).map((bracket, idx) => (
                      <tr key={idx} className="border-t border-border hover:bg-muted/20">
                        <td className="p-2">
                          <div className="flex items-center gap-1">
                            <DollarSign className="w-3.5 h-3.5 text-muted-foreground" />
                            <Input
                              type="number" value={bracket.min_value} className="h-8 w-28"
                              onChange={e => {
                                const nb = [...(staffingSettings.brackets || [])];
                                nb[idx] = { ...nb[idx], min_value: parseInt(e.target.value) || 0 };
                                setStaffingSettings({ ...staffingSettings, brackets: nb });
                              }}
                              data-testid={`bracket-min-${idx}`}
                            />
                          </div>
                        </td>
                        <td className="p-2">
                          <div className="flex items-center gap-1">
                            <DollarSign className="w-3.5 h-3.5 text-muted-foreground" />
                            <Input
                              type="number" value={bracket.max_value} className="h-8 w-28"
                              onChange={e => {
                                const nb = [...(staffingSettings.brackets || [])];
                                nb[idx] = { ...nb[idx], max_value: parseInt(e.target.value) || 0 };
                                setStaffingSettings({ ...staffingSettings, brackets: nb });
                              }}
                              data-testid={`bracket-max-${idx}`}
                            />
                          </div>
                        </td>
                        <td className="p-2 text-center">
                          <Input
                            type="number" min={1} max={20} value={bracket.max_artists} className="h-8 w-16 mx-auto text-center"
                            onChange={e => {
                              const nb = [...(staffingSettings.brackets || [])];
                              nb[idx] = { ...nb[idx], max_artists: parseInt(e.target.value) || 1 };
                              setStaffingSettings({ ...staffingSettings, brackets: nb });
                            }}
                            data-testid={`bracket-artists-${idx}`}
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            value={bracket.label || ""} className="h-8 w-32"
                            onChange={e => {
                              const nb = [...(staffingSettings.brackets || [])];
                              nb[idx] = { ...nb[idx], label: e.target.value };
                              setStaffingSettings({ ...staffingSettings, brackets: nb });
                            }}
                          />
                        </td>
                        <td className="p-2 text-right">
                          <button
                            onClick={() => {
                              const nb = (staffingSettings.brackets || []).filter((_, i) => i !== idx);
                              setStaffingSettings({ ...staffingSettings, brackets: nb });
                            }}
                            className="text-red-500 hover:text-red-700 p-1"
                            data-testid={`bracket-delete-${idx}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button variant="outline" size="sm" onClick={() => {
                const brackets = staffingSettings.brackets || [];
                const lastMax = brackets.length > 0 ? brackets[brackets.length - 1].max_value + 1 : 0;
                setStaffingSettings({
                  ...staffingSettings,
                  brackets: [...brackets, { min_value: lastMax, max_value: lastMax + 50000, max_artists: brackets.length + 1, label: "" }],
                });
              }} data-testid="add-bracket-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Bracket
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Fallback & Notes</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <SettingField label="Fallback Max Artists (when no proposal total)" value={staffingSettings.fallback_max_artists}
                onChange={v => setStaffingSettings({ ...staffingSettings, fallback_max_artists: parseInt(v) || 2 })} type="number" />
              <div className="space-y-1.5">
                <Label className="text-sm">Staffing Notes</Label>
                <Textarea
                  value={staffingSettings.notes || ""}
                  onChange={e => setStaffingSettings({ ...staffingSettings, notes: e.target.value })}
                  rows={2}
                  data-testid="staffing-notes"
                />
              </div>
            </CardContent>
          </Card>
          <div className="flex justify-end">
            <Button onClick={saveStaffingSettings} disabled={saving} data-testid="save-staffing-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Staffing Settings
            </Button>
          </div>
        </div>
      )}

      {/* TAB: Kickoff Phase */}
      {tab === "kickoff" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Kickoff Phase Configuration</CardTitle>
                <SettingToggle label="Active" value={kickoffSettings.active}
                  onChange={v => setKickoffSettings({ ...kickoffSettings, active: v })} />
              </div>
              <p className="text-sm text-muted-foreground">
                Pre-production alignment phase: onboarding, kickoff call scheduling, stakeholder alignment, scope confirmation, asset requests, and internal handoff.
              </p>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SettingField label="Display Name" value={kickoffSettings.display_name || "Kickoff"}
                  onChange={v => setKickoffSettings({ ...kickoffSettings, display_name: v })} />
                <SettingField label="Phase Name (System)" value={kickoffSettings.phase_name || "kickoff"}
                  onChange={v => setKickoffSettings({ ...kickoffSettings, phase_name: v })} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <SettingField label="Order Index" value={kickoffSettings.order_index ?? 1}
                  onChange={v => setKickoffSettings({ ...kickoffSettings, order_index: parseInt(v) || 1 })} type="number" />
                <SettingField label="Min Business Days" value={kickoffSettings.default_min_business_days ?? 3}
                  onChange={v => setKickoffSettings({ ...kickoffSettings, default_min_business_days: parseInt(v) || 1 })} type="number" />
                <SettingField label="Max Business Days" value={kickoffSettings.default_max_business_days ?? 5}
                  onChange={v => setKickoffSettings({ ...kickoffSettings, default_max_business_days: parseInt(v) || 1 })} type="number" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <SettingToggle label="Editable" value={kickoffSettings.editable}
                  onChange={v => setKickoffSettings({ ...kickoffSettings, editable: v })} />
                <SettingToggle label="Show in Proposals" value={kickoffSettings.proposal_visible}
                  onChange={v => setKickoffSettings({ ...kickoffSettings, proposal_visible: v })} />
                <SettingToggle label="Show in Projects" value={kickoffSettings.project_visible}
                  onChange={v => setKickoffSettings({ ...kickoffSettings, project_visible: v })} />
                <SettingToggle label="Include in Total Duration" value={kickoffSettings.include_in_total_timeline}
                  onChange={v => setKickoffSettings({ ...kickoffSettings, include_in_total_timeline: v })} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Complexity Scaling</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">Multiplier applied to kickoff duration based on project complexity.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {["simple", "standard", "luxury", "ultra_luxury"].map(level => (
                  <div key={level} className="space-y-1">
                    <Label className="text-xs capitalize">{level.replace("_", " ")}</Label>
                    <Input
                      type="number" step="0.1" min="0.5" max="3"
                      value={kickoffSettings.complexity_scaling?.[level] ?? 1.0}
                      onChange={e => setKickoffSettings({
                        ...kickoffSettings,
                        complexity_scaling: { ...kickoffSettings.complexity_scaling, [level]: parseFloat(e.target.value) || 1.0 }
                      })}
                      className="h-8"
                      data-testid={`kickoff-complexity-${level}`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Rush & Scaling</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <SettingField label="Rush Reduction (%)" value={kickoffSettings.rush_reduction_percent ?? 30}
                onChange={v => setKickoffSettings({ ...kickoffSettings, rush_reduction_percent: parseInt(v) || 0 })} type="number" />
              <div className="pt-2 border-t">
                <SettingToggle label="Stakeholder Scaling (Future)" value={kickoffSettings.stakeholder_scaling_enabled}
                  onChange={v => setKickoffSettings({ ...kickoffSettings, stakeholder_scaling_enabled: v })} />
                {kickoffSettings.stakeholder_scaling_enabled && (
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <SettingField label="Base Stakeholder Count" value={kickoffSettings.stakeholder_base_count ?? 3}
                      onChange={v => setKickoffSettings({ ...kickoffSettings, stakeholder_base_count: parseInt(v) || 3 })} type="number" />
                    <SettingField label="Extra Days Per Additional Stakeholder" value={kickoffSettings.stakeholder_extra_days_per ?? 0.5}
                      onChange={v => setKickoffSettings({ ...kickoffSettings, stakeholder_extra_days_per: parseFloat(v) || 0.5 })} type="number" />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Internal Notes</CardTitle></CardHeader>
            <CardContent>
              <Textarea
                value={kickoffSettings.internal_notes || ""}
                onChange={e => setKickoffSettings({ ...kickoffSettings, internal_notes: e.target.value })}
                rows={3}
                placeholder="Onboarding alignment, kickoff call scheduling/completion, stakeholder alignment, initial scope confirmation, asset request/initiation, internal handoff into production readiness"
                data-testid="kickoff-notes"
              />
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={saveKickoffSettings} disabled={saving} data-testid="save-kickoff-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Kickoff Settings
            </Button>
          </div>
        </div>
      )}

      {/* TAB: Buffers */}
      {tab === "buffers" && (
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Ramp-Up & Project Setup Period</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SettingField label="Ramp-Up & Setup Days" value={bufferSettings.kickoff_ramp_up_days ?? 10}
                onChange={v => setBufferSettings({ ...bufferSettings, kickoff_ramp_up_days: parseInt(v) || 10 })} type="number" />
              <div className="text-sm text-muted-foreground flex items-center">
                Default 10 days (2 weeks) between project kickoff and production start. Reduced by rush buffer for rush projects.
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Review & Revision Buffers</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SettingField label="Client Review Turnaround (days)" value={bufferSettings.client_review_turnaround_days}
                onChange={v => setBufferSettings({ ...bufferSettings, client_review_turnaround_days: parseInt(v) || 3 })} type="number" />
              <SettingField label="Internal QA (days)" value={bufferSettings.internal_qa_days}
                onChange={v => setBufferSettings({ ...bufferSettings, internal_qa_days: parseInt(v) || 2 })} type="number" />
              <SettingField label="Revision Turnaround (days)" value={bufferSettings.revision_turnaround_days}
                onChange={v => setBufferSettings({ ...bufferSettings, revision_turnaround_days: parseInt(v) || 2 })} type="number" />
              <SettingField label="Final Export/Packaging (days)" value={bufferSettings.final_export_packaging_days}
                onChange={v => setBufferSettings({ ...bufferSettings, final_export_packaging_days: parseInt(v) || 2 })} type="number" />
              <SettingField label="Contingency Buffer (%)" value={bufferSettings.contingency_buffer_percent}
                onChange={v => setBufferSettings({ ...bufferSettings, contingency_buffer_percent: parseInt(v) || 15 })} type="number" />
              <SettingField label="Approval Delay — Proposal (days)" value={bufferSettings.approval_delay_days_proposal}
                onChange={v => setBufferSettings({ ...bufferSettings, approval_delay_days_proposal: parseInt(v) || 3 })} type="number" />
              <SettingField label="Rush Buffer Reduction (%)" value={bufferSettings.rush_buffer_reduction_percent}
                onChange={v => setBufferSettings({ ...bufferSettings, rush_buffer_reduction_percent: parseInt(v) || 50 })} type="number" />
            </CardContent>
          </Card>
          <div className="flex justify-end">
            <Button onClick={saveBufferSettings} disabled={saving} data-testid="save-buffer-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Buffer Settings
            </Button>
          </div>
        </div>
      )}

      {/* TAB: Display / Proposal */}
      {tab === "display" && (
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Proposal Output Settings</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm">Display Unit</Label>
                  <Select value={displaySettings.display_unit || "weeks"} onValueChange={v => setDisplaySettings({ ...displaySettings, display_unit: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weeks">Weeks</SelectItem>
                      <SelectItem value="days">Business Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SettingToggle label="Show Range Only" value={displaySettings.show_range_only}
                  onChange={v => setDisplaySettings({ ...displaySettings, show_range_only: v })} />
                <SettingToggle label="Show Phase Breakdown" value={displaySettings.show_phase_breakdown}
                  onChange={v => setDisplaySettings({ ...displaySettings, show_phase_breakdown: v })} />
                <SettingToggle label="Show Ramp-Up Note" value={displaySettings.show_ramp_up_note}
                  onChange={v => setDisplaySettings({ ...displaySettings, show_ramp_up_note: v })} />
                <SettingToggle label="Show Confidence Indicator" value={displaySettings.show_confidence_indicator}
                  onChange={v => setDisplaySettings({ ...displaySettings, show_confidence_indicator: v })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Proposal Disclaimer Text</Label>
                <Textarea
                  value={displaySettings.proposal_disclaimer_text || ""}
                  onChange={e => setDisplaySettings({ ...displaySettings, proposal_disclaimer_text: e.target.value })}
                  rows={3}
                  data-testid="disclaimer-text"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Scheduling Note</Label>
                <Textarea
                  value={displaySettings.proposal_scheduling_note || ""}
                  onChange={e => setDisplaySettings({ ...displaySettings, proposal_scheduling_note: e.target.value })}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
          <div className="flex justify-end">
            <Button onClick={saveDisplaySettings} disabled={saving} data-testid="save-display-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Display Settings
            </Button>
          </div>
        </div>
      )}

      {/* Rule Edit Dialog */}
      <RuleEditDialog
        open={showRuleDialog}
        onClose={() => { setShowRuleDialog(false); setEditingRule(null); }}
        rule={editingRule}
        onSave={saveRule}
        saving={saving}
        roleBuckets={roleBuckets}
      />
    </div>
  );
}

// ============================================================
// SUBCOMPONENTS
// ============================================================

function SettingField({ label, value, onChange, type = "text" }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={e => onChange(e.target.value)} className="h-9" />
    </div>
  );
}

function SettingToggle({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between py-1">
      <Label className="text-sm">{label}</Label>
      <button onClick={() => onChange(!value)} className="text-muted-foreground hover:text-foreground">
        {value ? <ToggleRight className="w-6 h-6 text-green-600" /> : <ToggleLeft className="w-6 h-6" />}
      </button>
    </div>
  );
}

function RuleEditDialog({ open, onClose, rule, onSave, saving, roleBuckets }) {
  const [form, setForm] = useState({});
  
  useEffect(() => {
    if (rule) {
      setForm({ ...rule });
    } else {
      setForm({
        deliverable_name: "", category: "3D Modeling", phase_default: 3,
        role_bucket_required: "3D Modeling", base_duration_days: 1, duration_per_unit: 1,
        minimum_duration_days: 0.5, setup_duration_days: 0, revision_rounds_included: 2,
        revision_buffer_percent: 25, dependency_type: "none", can_run_in_parallel: true,
        can_start_during_ramp: false, max_parallel_artists: null, complexity_multiplier_simple: 0.8,
        complexity_multiplier_standard: 1.0, complexity_multiplier_luxury: 1.3,
        complexity_multiplier_ultra_luxury: 1.6, rush_multiplier: 1.0,
        existing_asset_reduction_factor: 0.5, repeated_unit_reduction_factor: 0.85,
        active: true, internal_notes: "",
      });
    }
  }, [rule]);

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{rule ? "Edit Deliverable Rule" : "New Deliverable Rule"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-sm">Deliverable Name *</Label>
              <Input value={form.deliverable_name || ""} onChange={e => update("deliverable_name", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Category</Label>
              <Select value={form.category || ""} onValueChange={v => update("category", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Phase</Label>
              <Select value={String(form.phase_default ?? 3)} onValueChange={v => update("phase_default", parseInt(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PHASES.map((p, i) => <SelectItem key={i} value={String(i)}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Role Bucket</Label>
              <Select value={form.role_bucket_required || ""} onValueChange={v => update("role_bucket_required", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{roleBuckets.map(b => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Dependency Type</Label>
              <Select value={form.dependency_type || "none"} onValueChange={v => update("dependency_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DEP_TYPES.map(d => <SelectItem key={d} value={d}>{d === "none" ? "None" : d.replace("requires_", "").replaceAll("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <SettingField label="Days/Unit" value={form.duration_per_unit} onChange={v => update("duration_per_unit", parseFloat(v) || 1)} type="number" />
            <SettingField label="Base Duration" value={form.base_duration_days} onChange={v => update("base_duration_days", parseFloat(v) || 1)} type="number" />
            <SettingField label="Min Days" value={form.minimum_duration_days} onChange={v => update("minimum_duration_days", parseFloat(v) || 0.5)} type="number" />
            <SettingField label="Setup Days" value={form.setup_duration_days} onChange={v => update("setup_duration_days", parseFloat(v) || 0)} type="number" />
            <SettingField label="Revision Rounds" value={form.revision_rounds_included} onChange={v => update("revision_rounds_included", parseInt(v) || 2)} type="number" />
            <SettingField label="Revision Buffer %" value={form.revision_buffer_percent} onChange={v => update("revision_buffer_percent", parseFloat(v) || 25)} type="number" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SettingField label="Simple x" value={form.complexity_multiplier_simple} onChange={v => update("complexity_multiplier_simple", parseFloat(v) || 0.8)} type="number" />
            <SettingField label="Standard x" value={form.complexity_multiplier_standard} onChange={v => update("complexity_multiplier_standard", parseFloat(v) || 1.0)} type="number" />
            <SettingField label="Luxury x" value={form.complexity_multiplier_luxury} onChange={v => update("complexity_multiplier_luxury", parseFloat(v) || 1.3)} type="number" />
            <SettingField label="Ultra Lux x" value={form.complexity_multiplier_ultra_luxury} onChange={v => update("complexity_multiplier_ultra_luxury", parseFloat(v) || 1.6)} type="number" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <SettingField label="Rush x" value={form.rush_multiplier} onChange={v => update("rush_multiplier", parseFloat(v) || 1.0)} type="number" />
            <SettingField label="Existing Asset x" value={form.existing_asset_reduction_factor} onChange={v => update("existing_asset_reduction_factor", parseFloat(v) || 0.5)} type="number" />
            <SettingField label="Repeat Unit x" value={form.repeated_unit_reduction_factor} onChange={v => update("repeated_unit_reduction_factor", parseFloat(v) || 0.85)} type="number" />
          </div>
          <div className="flex gap-4">
            <SettingToggle label="Can Run in Parallel" value={form.can_run_in_parallel} onChange={v => update("can_run_in_parallel", v)} />
            <SettingToggle label="Can Start During Ramp" value={form.can_start_during_ramp} onChange={v => update("can_start_during_ramp", v)} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <SettingField label="Max Parallel Artists" value={form.max_parallel_artists ?? ""}
              onChange={v => update("max_parallel_artists", v === "" ? null : parseInt(v) || null)}
              type="number" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Internal Notes</Label>
            <Textarea value={form.internal_notes || ""} onChange={e => update("internal_notes", e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={saving || !form.deliverable_name} data-testid="save-rule-btn">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {rule ? "Update Rule" : "Create Rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// TIMELINE VIEW — visual phase-grouped card layout (replaces the dense default
// table). Cards are color-coded by deliverable category. Clicking a card opens
// the existing RuleEditDialog so all editing flows stay consistent.
// =============================================================================
function TimelineView({ rules, onCardClick, onToggle, onDelete }) {
  // Group rules using the central resolver (category + name + phase_default).
  const groups = PHASE_GROUPS.map(g => ({
    ...g,
    rules: rules.filter(r => findPhaseGroupForRule(r)?.key === g.key)
  }));

  if (rules.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-border rounded-lg">
        <Layers className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
        <p className="text-sm text-muted-foreground">No rules match your filter.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="timeline-view-container">
      {/* Phase rail header — compact horizontal map of all 5 phases */}
      <div className="flex items-stretch gap-1 rounded-lg border border-border bg-muted/20 p-1.5 overflow-x-auto" data-testid="phase-rail">
        {groups.map((g, idx) => {
          const Icon = g.icon;
          // Count active-only so deprecated library items (e.g., the 9
          // sunset VEXOS deliverables) don't inflate the section badge.
          const count = g.rules.filter(r => r.active !== false).length;
          return (
            <div key={g.key} className="flex-1 min-w-[120px] flex items-center gap-2 px-2.5 py-1.5">
              <Icon className={`w-3.5 h-3.5 ${g.accent}`} />
              <div className="min-w-0">
                <div className="text-[11px] font-semibold tracking-tight truncate">{g.label}</div>
                <div className="text-[10px] text-muted-foreground">{count} {count === 1 ? "rule" : "rules"}</div>
              </div>
              {idx < groups.length - 1 && (
                <ChevronRight className="w-3 h-3 text-muted-foreground/40 ml-auto" />
              )}
            </div>
          );
        })}
      </div>

      {/* Phase sections */}
      {groups.map(g => (
        <PhaseSection
          key={g.key}
          group={g}
          rules={g.rules}
          onCardClick={onCardClick}
          onToggle={onToggle}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

function PhaseSection({ group, rules, onCardClick, onToggle, onDelete }) {
  const Icon = group.icon;
  return (
    <section className="rounded-lg border border-border bg-card overflow-hidden" data-testid={`phase-section-${group.key}`}>
      <header className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/20">
        <div className="flex items-center gap-2.5">
          <div className={`w-7 h-7 rounded-md flex items-center justify-center bg-background border border-border ${group.accent}`}>
            <Icon className="w-3.5 h-3.5" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold tracking-tight leading-tight">{group.label}</h3>
            <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">{group.subtitle}</p>
          </div>
        </div>
        <Badge variant="outline" className="text-[10px]" data-testid={`phase-count-${group.key}`}>{rules.filter(r => r.active !== false).length}</Badge>
      </header>

      {rules.length === 0 ? (
        <div className="px-4 py-6 text-[11px] text-muted-foreground italic">No deliverables in this phase.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 p-3">
          {rules.map(rule => (
            <RuleCard key={rule.id} rule={rule} onClick={() => onCardClick(rule)} onToggle={() => onToggle(rule)} onDelete={onDelete ? () => onDelete(rule.id, rule.deliverable_name) : null} />
          ))}
        </div>
      )}
    </section>
  );
}

function RuleCard({ rule, onClick, onToggle, onDelete }) {
  const style = CATEGORY_STYLE[rule.category] || DEFAULT_CATEGORY_STYLE;
  const Icon = style.icon;
  const dep = formatDepLabel(rule.dependency_type);
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`rule-card-${rule.id}`}
      className={`group relative text-left rounded-lg border border-border bg-card hover:shadow-md hover:-translate-y-0.5 hover:border-foreground/20 transition-all duration-200 overflow-hidden ${!rule.active ? "opacity-50" : ""}`}
    >
      {/* Left color bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${style.bar}`} />
      <div className="pl-3.5 pr-3 py-2.5">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <Icon className={`w-3 h-3 shrink-0 ${style.text}`} />
            <span className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground truncate">{rule.category}</span>
            {(rule.deprecated || rule.active === false) && (
              <span className="ml-1 inline-flex items-center px-1 py-0.5 rounded text-[8.5px] font-semibold tracking-[0.10em] uppercase bg-amber-500/10 text-amber-700 border border-amber-500/25" data-testid={`deprecated-badge-${rule.id}`}>
                {rule.deprecated ? "Deprecated" : "Hidden"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {onDelete && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="text-muted-foreground/40 hover:text-red-500 transition-colors p-0.5 -m-0.5 rounded"
                data-testid={`rule-card-delete-${rule.id}`}
                title="Permanently delete this rule"
                aria-label="Delete rule"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggle(); }}
              className="text-muted-foreground hover:text-foreground"
              data-testid={`rule-card-toggle-${rule.id}`}
              aria-label="Toggle active"
            >
              {rule.active ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <h4 className="text-[13px] font-semibold leading-tight tracking-tight mb-1.5 line-clamp-2">{rule.deliverable_name}</h4>
        <div className="flex items-center justify-between gap-2 text-[11px]">
          <span className="font-mono text-foreground/80">{rule.duration_per_unit}d <span className="text-muted-foreground/70">/ unit</span></span>
          {rule.max_parallel_artists != null && (
            <span className="text-muted-foreground">·</span>
          )}
          {rule.max_parallel_artists != null && (
            <span className="text-muted-foreground tabular-nums">max {rule.max_parallel_artists} artists</span>
          )}
        </div>
        {dep && (
          <div className="mt-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted text-[10px] text-muted-foreground">
            <span className="opacity-60">↳</span>{dep}
          </div>
        )}
      </div>
    </button>
  );
}

